import _ from 'lodash';
import * as MODEL from '~/constants/ModelConstants';

const StringService = function() {
  const normalize = val => {
    if (typeof val === 'string' || val instanceof String) {
      return val.toLowerCase();
    } else {
      return typeof val !== 'undefined' ? val.toString() : '';
    }
  };

  const join = (array, seperator) => {
    return array.join(seperator);
  };

  const capitalize = string => {
    return string.charAt(0).toUpperCase() + string.substring(1).toLowerCase();
  };

  const trimToLength = function(string, maxLength, delimiter) {
    if (!string || !string.length || !maxLength || !delimiter) {
      return string;
    }

    let parts = _.trim(string, delimiter).split(delimiter),
      result;

    for (let i = parts.length; i > 0; i--) {
      result = parts.slice(0, i).join(delimiter);
      if (result.length <= maxLength) {
        return result;
      }
    }

    return result.substr(0, maxLength);
  };

  const trimAndSanitizeTitle = function(title, limit, delimiter) {
    let sanitizedTitle = title
      .replace(/\b(a|an|and|the|of|or|in|is|on|by)\b/g, '')
      .trim()
      .replace(/[\W_]+/g, '-');
    return trimToLength(sanitizedTitle, limit, delimiter);
  };

  const sanitizeTitle = title => {
    return title
      .replace(/\b(a|an|and|the|of|or|in|is|on|by)\b/g, '')
      .trim()
      .replace(/[\W_]+/g, '-');
  };

  const getSEOVulnItemDetails = (title, language, id) => {
    if (!language || !title || !id) {
      return;
    }

    language = MODEL.languageToUrl[language.toLowerCase()]
      ? MODEL.languageToUrl[language.toLowerCase()]
      : language;

    let displayIssue = sanitizeTitle(title);

    return {
      title: normalize(displayIssue),
      language: normalize(language),
      vulnId: id,
    };
  };

  const getRedditShareLink = (title, link) => {
    return 'https://reddit.com/submit?url=' + link + '&title=' + encodeURIComponent(title);
  };

  const getTwitterShareLink = (title, link) => {
    return 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(title) + '&url=' + link;
  };

  const getLinkedInShareLink = (title, link) => {
    return (
      'https://www.linkedin.com/shareArticle?mini=true&url=' +
      link +
      '&title=' +
      encodeURIComponent(title) +
      '&source=SourceClear'
    );
  };

  const createSEOVulnItemUrl = (description, language, id) => {
    let data = getSEOVulnItemDetails(description, language, id);
    return `/${MODEL.SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/security/${data.title}/${
      data.language
    }/sid-${data.vulnId}`;
  };

  // returns an object that can be used as a $stateParam or to manually construct a URL like the next function
  const getSEOComponentItemDetails = function(title, language, id, packageManager) {
    if (!title || !language || !id) {
      return;
    }

    language = MODEL.languageToUrl[language.toLowerCase()]
      ? MODEL.languageToUrl[language.toLowerCase()]
      : language;

    let displayTitle = trimAndSanitizeTitle(title, 40, '-');

    packageManager = packageManager.toLowerCase();

    return {
      title: normalize(displayTitle),
      language: normalize(language),
      packageManager: packageManager,
      componentId: id,
    };
  };

  const createSEOComponentItemUrl = (title, language, id, packageManager) => {
    let data = getSEOComponentItemDetails(title, language, id, packageManager) || {};

    //If title is empty, return the url using componentId
    //This prevents returning a broken url - one without title
    if (data && !data.title) {
      return `/${MODEL.SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/libraries/${
        data.componentId
      }`;
    }

    return `/${MODEL.SOURCECLEAR_COMPONENTS.VULNERABILITY_DATABASE.slug}/libraries/${data.title}/${
      data.language
    }/${data.packageManager}/lid-${data.componentId}`;
  };

  const makeOrgName = org => {
    const domain =
      org.billingEmail && org.billingEmail.indexOf('@') !== -1
        ? org.billingEmail.split('@')[1]
        : '';
    return org.displayName + (domain && domain.length > 0 ? ' [' + domain + ']' : '');
  };

  const stripParens = input => {
    return input.replace(/[()]/g, '');
  };

  const isUrlString = string => {
    let urlPattern = /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/; // eslint-disable-line no-useless-escape
    return urlPattern.test(string);
  };

  /*
   * params:
   * array ie ['A', 'B', 'C']
   * optional conjunction ie 'and' , 'or'
   *
   * returns 'A, B and C' for default conjunction 'and'
   */
  const commaConjunctionJoinString = (array, conjunction = 'and') => {
    const arr = array.map((item, index) => {
      const maybeCommaSeparator =
        array.length > 2 && index < array.length - 1 ? ', ' : ` ${conjunction} `;
      const separator = index === 0 ? '' : maybeCommaSeparator;

      return `${separator}${item}`;
    });

    return arr.join('');
  };

  return {
    normalize,
    join,
    capitalize,
    sanitizeTitle,
    getSEOVulnItemDetails,
    getRedditShareLink,
    getTwitterShareLink,
    getLinkedInShareLink,
    createSEOVulnItemUrl,
    getSEOComponentItemDetails,
    createSEOComponentItemUrl,
    makeOrgName,
    stripParens,
    isUrlString,
    commaConjunctionJoinString,
  };
};

export default new StringService();
